import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../utils/translations';
import './Login.css';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [clientOptions, setClientOptions] = useState(null);
  const [selectionToken, setSelectionToken] = useState('');
  const [pendingUser, setPendingUser] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState('');
  const { login, selectClient, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(username, password);
    setLoading(false);

    if (result.needsClientSelection) {
      setClientOptions(result.clients || []);
      setSelectionToken(result.selectionToken || '');
      setPendingUser(result.pendingUser || null);
      setSelectedClientId(result.clients?.[0]?.id || '');
      return;
    }

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.message || 'Login failed');
    }
  };

  const handleSelectClient = async (e) => {
    e.preventDefault();
    if (!selectedClientId) {
      setError('Please select a client');
      return;
    }
    setError('');
    setLoading(true);
    const result = await selectClient(selectionToken, selectedClientId);
    setLoading(false);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.message || 'Failed to select client');
    }
  };

  const handleBackToLogin = () => {
    setClientOptions(null);
    setSelectionToken('');
    setPendingUser(null);
    setSelectedClientId('');
    setError('');
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1>{t('Inventory Management System')}</h1>
        {clientOptions ? (
          <>
            <h2>Select client</h2>
            {pendingUser?.full_name ? (
              <p className="login-client-hint">Signed in as {pendingUser.full_name}. Choose which client to open.</p>
            ) : (
              <p className="login-client-hint">This account belongs to more than one client. Choose which one to open.</p>
            )}
            {error && <div className="alert alert-error">{error}</div>}
            <form onSubmit={handleSelectClient}>
              <div className="form-group">
                <label>Client</label>
                <select
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  required
                  autoFocus
                >
                  {clientOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.display_name || c.alias_name || c.name}
                      {c.alias_name && c.name && c.alias_name !== c.name ? ` (${c.name})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                {loading ? 'Opening…' : 'Continue'}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-block"
                style={{ marginTop: 10 }}
                onClick={handleBackToLogin}
                disabled={loading}
              >
                Back
              </button>
            </form>
          </>
        ) : (
          <>
            <h2>{t('Login')}</h2>
            {error && <div className="alert alert-error">{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>{t('Username or Email')}</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>{t('Password')}</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                {loading ? t('Logging in...') : t('Login')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
